import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ApiEndpoints } from 'services/endpoints';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

export function useGetProducts(params = {}) {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);

    const paramsKey = JSON.stringify(params);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setIsError(false);

        const cleanParams = Object.fromEntries(
            Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''),
        );

        axios
            .get(`${BASE_URL}${ApiEndpoints.GET_PRODUCTS}`, { params: cleanParams })
            .then((res) => {
                if (!cancelled) {
                    const d = res.data?.data ?? res.data ?? {};
                    setData({
                        items: d.items ?? [],
                        code: d.code,
                        message: d.message,
                        filter_applied: d.filter_applied ?? null,
                        page_context: d.page_context ?? null,
                    });
                }
            })
            .catch(() => {
                if (!cancelled) setIsError(true);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paramsKey]);

    return { data, isLoading, isError };
}

export function useGetProductById(id) {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);

    useEffect(() => {
        if (!id) return;
        let cancelled = false;
        setIsLoading(true);
        setIsError(false);
        setData(null);

        axios
            .get(`${BASE_URL}${ApiEndpoints.GET_PRODUCT_DETAIL}`, { params: { product_id: id } })
            .then((res) => {
                if (!cancelled) setData(res.data?.data?.item ?? null);
            })
            .catch(() => {
                if (!cancelled) setIsError(true);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [id]);

    return { data, isLoading, isError };
}

export function useGetVariantById(variantItemId) {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const cacheRef = useRef({});

    useEffect(() => {
        if (!variantItemId) {
            setData(null);
            setIsLoading(false);
            return;
        }

        if (cacheRef.current[variantItemId]) {
            setData(cacheRef.current[variantItemId]);
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        setIsLoading(true);
        setIsError(false);

        axios
            .get(`${BASE_URL}${ApiEndpoints.GET_PRODUCT_DETAIL}`, {
                params: { product_id: variantItemId },
            })
            .then((res) => {
                const item = res.data?.data?.item ?? null;
                if (!cancelled) {
                    if (item) cacheRef.current[variantItemId] = item;
                    setData(item);
                }
            })
            .catch(() => {
                if (!cancelled) setIsError(true);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [variantItemId]);

    return { data, isLoading, isError };
}

function normaliseAttributes(group) {
    if (Array.isArray(group.attributes) && group.attributes.length > 0) {
        return group.attributes;
    }

    const attrMap = {};

    const registerAttr = (id, name, optionId, optionName) => {
        if (!id || !name) return;
        if (!attrMap[id]) attrMap[id] = { id, name, type: 'Text', options: [] };
        if (optionId && optionName && !attrMap[id].options.some((o) => o.id === optionId)) {
            attrMap[id].options.push({ id: optionId, name: optionName });
        }
    };

    (group.items || []).forEach((item) => {
        registerAttr(
            group.attribute_id1,
            group.attribute_name1,
            item.attribute_option_id1,
            item.attribute_option_name1,
        );
        registerAttr(
            group.attribute_id2,
            group.attribute_name2,
            item.attribute_option_id2,
            item.attribute_option_name2,
        );
    });

    return Object.values(attrMap).filter((a) => a.options.length > 0);
}
export function useGetItemGroup(groupId) {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const cacheRef = useRef({});

    useEffect(() => {
        if (!groupId) {
            setData(null);
            return;
        }

        if (cacheRef.current[groupId]) {
            setData(cacheRef.current[groupId]);
            return;
        }

        let cancelled = false;
        setIsLoading(true);
        setIsError(false);
        setData(null);


        axios
            .get(`${BASE_URL}${ApiEndpoints.GROUPED_PRODUCTS_DETAIL}`, {
                params: { group_id: groupId },
            })
            .then((res) => {
                if (cancelled) return;

                const listGroups = res.data?.data?.itemgroups;
                if (Array.isArray(listGroups) && listGroups.length > 0) {
                    const group =
                        listGroups.find((g) => String(g.group_id) === String(groupId)) ??
                        listGroups[0];

                    if (group) {
                        const normalised = {
                            ...group,
                            attributes: normaliseAttributes(group),
                        };
                        cacheRef.current[groupId] = normalised;
                        setData(normalised);
                    } else {
                        setData(null);
                    }
                    return;
                }

                const singleGroup =
                    res.data?.data?.item_group ??  
                    res.data?.item_group ??  
                    null;

                if (singleGroup) {
                    const normalised = {
                        ...singleGroup,
                        attributes: normaliseAttributes(singleGroup),
                    };
                    cacheRef.current[groupId] = normalised;
                    setData(normalised);
                } else {
                    const fallback = res.data?.data ?? res.data;
                    if (fallback?.group_id) {
                        const normalised = {
                            ...fallback,
                            attributes: normaliseAttributes(fallback),
                        };
                        cacheRef.current[groupId] = normalised;
                        setData(normalised);
                    } else {
                        setData(null);
                    }
                }
            })
            .catch(() => {
                if (!cancelled) setIsError(true);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [groupId]);

    return { data, isLoading, isError };
}
